// Tells the server what the player is doing, so each listen is one play:
// start on play, pause on pause (the server keeps the play open and counts
// only time listened), stop on end or next. Pausing used to stop the play,
// which split one listen into two payments and two listens.
//
// While a song plays, a heartbeat every 30 s tells the server the listener is
// still there. When the tab closes mid-song no stop arrives, and the play ends
// at the last heartbeat instead of being lost.
const HEARTBEAT_MS = 30 * 1000;

export function createPlayTracking(api, { heartbeatMs = HEARTBEAT_MS } = {}) {
  let activeId = null;
  let paused = false;
  let heartbeat = null;

  const stopHeartbeat = () => {
    clearInterval(heartbeat);
    heartbeat = null;
  };

  const startHeartbeat = (song) => {
    stopHeartbeat();
    heartbeat = setInterval(async () => {
      try {
        const response = await api.STREAM_HEARTBEAT({ musicId: song._id });
        // The server already ended this play (the computer slept, say): the rest of the song is a new one.
        if (response?.result?.active === false && activeId === song._id && !paused) {
          activeId = null;
          await tracking.start(song);
        }
      } catch (_) {
        // The next heartbeat tries again.
      }
    }, heartbeatMs);
  };

  const tracking = {
    /** musicId of the song with an open play, or null. */
    get activeId() {
      return activeId;
    },

    async start(song) {
      if (!song?._id) return;
      if (activeId === song._id && !paused) return;
      // Another song is taking over: finish its play, or it is never counted.
      if (activeId && activeId !== song._id) {
        const previous = activeId;
        activeId = null;
        stopHeartbeat();
        api.STREAM_STOP({ musicId: previous }).catch(() => {});
      }
      // Starting a paused song resumes the same play on the server.
      await api.STREAM_START({
        musicId: song._id,
        playlistId: song.playListId || undefined,
        albumId: song.albumId || undefined,
      });
      activeId = song._id;
      paused = false;
      startHeartbeat(song);
    },

    async pause(song) {
      if (!song?._id || activeId !== song._id || paused) return;
      paused = true;
      stopHeartbeat();
      await api.STREAM_PAUSE({ musicId: song._id });
    },

    /** Ends the play; resolves to the server's response, or null when nothing was open. */
    async stop(song) {
      if (!song?._id || activeId !== song._id) return null;
      activeId = null; // clear first so a second stop is a no-op
      paused = false;
      stopHeartbeat();
      return api.STREAM_STOP({ musicId: song._id });
    },
  };

  return tracking;
}
